/* eslint complexity: ["error", {max: 23}] -- this overrides for the entire file */
/* eslint max-statements: ["error", {max: 46}] -- applies to entire file */
/* eslint max-lines-per-function: ["error", {max: 290}] -- applies to entire file */
/* eslint max-lines: ["error", {max: 410}] -- applies to entire file */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useIsFocused } from "@react-navigation/native";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import shuffle from "lodash/shuffle";
import { RefreshControl } from "react-native";

import {
  AnalyticsComponent,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { TabSources } from "@/constants/analytics/Tabs";
import { HomeTabSubDeepLinks } from "@/constants/Home";
import { ModalPath, TabPath } from "@/constants/Navigation";
import { DeepLinkParams } from "@/constants/Params";
import { FABTestID } from "@/constants/TestID";
import { useSchedulePermissionsContext } from "@/context-providers/SchedulePermissionsContext";
import { router } from "@/crossPlatform/expo-router";
import useRestoreReturnToUrl from "@/hooks/browser/useRestoreReturnToUrl";
import { useDeepLinkingContext } from "@/hooks/deepLinking/useDeepLinkingContext";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useAppUserContentFeed from "@/hooks/useAppUserContentFeed";
import useAppUserSMTPermissions from "@/hooks/useAppUserSMTPermissions";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useDownloadEnforcementModal from "@/hooks/useDownloadEnforcementModal";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useHelpCenter from "@/hooks/useHelpCenter";
import useHomeEntryModals from "@/hooks/useHomeEntryModals";
import { useModalTracker } from "@/hooks/useModalTracker";
import usePatientStatus from "@/hooks/usePatientStatus";
import { useRestorePushNotifications } from "@/hooks/useRestorePushNotifications";
import useShouldShowOnboarding from "@/hooks/useShouldShowOnboarding";
import useWebRedesignOverviewModal from "@/hooks/useWebRedesignOverviewModal";
import { useAnalytics } from "@/services/Analytics";
import api, { CacheIdType, CacheTagType } from "@/store/api";
import { useGetTasksQuery } from "@/store/api/tasks";
import { useGetBasicInfoQuery, useGetFullProfileQuery } from "@/store/api/user";
import { useAppDispatch } from "@/store/hooks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import ExploreResourcesCarousel from "@/ui/components/explore/ExploreResourcesCarousel";
import { GreetingCard } from "@/ui/components/home/GreetingCard";
import RecommendedArticle from "@/ui/components/home/RecommendedArticle";
import RecommendedFAQ from "@/ui/components/home/RecommendedFAQ";
import RecommendedGroupsCarousel from "@/ui/components/home/RecommendedGroupsCarousel";
import TestimonialImage from "@/ui/components/home/TestimonialImage";
import UpcomingEventsSection from "@/ui/components/home/UpcomingEventsSection";
import { OnboardingOverviewSection } from "@/ui/components/onboarding/v2/OnboardingOverviewSection";
import MealSupportHotlineSection from "@/ui/components/profile/MealSupportHotlineSection";
import AddSMTEntryFlow from "@/ui/components/smt/AddSMTEntry/AddSMTEntryFlow";
import SMTWeekLogSection from "@/ui/components/smt/SMTWeekLogSection";
import SMTWelcomeModal from "@/ui/components/smt/SMTWelcomeModal";
import TasksCarousel from "@/ui/components/tasks/TasksCarousel";
import WeightTrackingSection from "@/ui/components/vitals/WeightTrackingSection";
import DownloadEnforcementModal from "@/ui/components/web/DownloadEnforcementModal";
import WebRedesignOverviewModal from "@/ui/components/web/WebRedesignOverviewModal";
import { FAIconName, FloatingButton } from "@/ui/core";
import {
  ScreenContainer,
  ScrollView,
  VStack,
  WebContentConstraints,
} from "@/ui/layout";
import { ListDimensions } from "@/ui/layout/list/ListComponents";
import { ColorToken, ColorTokenValues } from "@/ui/theme/tokens";
import { mapFullProfileToUserInfo } from "@/utils/analytics.util";
import { encodeNavigationParams } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import { getMostPopularVideoResources } from "@/utils/resource.util";
import {
  getNotificationSettings,
  getPreferredFirstName,
} from "@/utils/user.util";

const useJournalSubDeepLink = () => {
  const {
    [DeepLinkParams.EntryId]: entryId,
    deepLinkUrl,
    subtarget,
  } = useEncodedSearchParams<{
    deepLinkUrl?: string;
    "entry-id"?: string;
    subtarget?: string;
  }>();

  const handleOpenJournal = useCallback(() => {
    router.navigate({
      params: encodeNavigationParams({
        [DeepLinkParams.EntryId]: entryId,
        deepLinkUrl,
        subtarget,
      }),
      pathname: ModalPath.Journal,
    });
  }, [deepLinkUrl, entryId, subtarget]);

  useSubDeepLink({
    analyticsEvent: SMTEntryAnalyticsEvent.ViewAllEntriesOpenedFromDeepLink,
    analyticsProps: {
      component: AnalyticsComponent.Tab,
      source: TabSources.Home,
    },
    onOpen: handleOpenJournal,
    targetValue: HomeTabSubDeepLinks.Journal,
  });
};

const Home = () => {
  const isFocused = useIsFocused();
  const { markPostLoginReadyForDeepLinks } = useDeepLinkingContext();
  useEffect(() => {
    markPostLoginReadyForDeepLinks();
  }, [markPostLoginReadyForDeepLinks]);

  // on web, only AFTER reaching the home tab:
  // restore the user to the URL they visited before they were redirected to Auth0's login page
  useRestoreReturnToUrl();

  const [name, setName] = useState<string>();
  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettings>();
  const { trackHomeTabViewed, updateUserInfo } = useAnalytics();
  const { data: basicInfoData, refetch: refetchBasicInfo } =
    useGetBasicInfoQuery();
  const { trackStartBooking } = useBookingAnalytics();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();
  const { shouldShowOnboarding } = useShouldShowOnboarding();
  useRestorePushNotifications({ notificationSettings });
  const {
    externalId: appUserId,
    isPatient,
    isSupport,
    universalStatusCode,
  } = useAppUserSelector();
  const { isRecommendedGroupCheckComplete } = useHomeEntryModals();
  const { handleWebRedesignOverviewDismiss, shouldShowWebRedesignOverview } =
    useWebRedesignOverviewModal(appUserId);
  const {
    handleDownloadEnforcementClose,
    handleDownloadEnforcementDownload,
    isDownloadEnforcementDesktop,
    isDownloadEnforcementModalOpen,
  } = useDownloadEnforcementModal(appUserId);
  const proxyPatients = useAppUserProxyPatientsSelector();

  const { refetch: refetchTasks } = useGetTasksQuery({
    appUserId,
    isPatient,
    linkedPatients: proxyPatients ?? [],
  });

  const {
    refetchSMTPermissions,
    shouldAppUserSeeAddSMTEntriesButton,
    shouldAppUserSeeWeekLogSection,
  } = useAppUserSMTPermissions();
  const shouldShowMealTracking = shouldAppUserSeeAddSMTEntriesButton;

  // Animation state for FAB bounce after welcome modal dismiss
  const [shouldAnimateFAB, setShouldAnimateFAB] = useState<boolean>(false);
  const handleFABAnimationTrigger = () => setShouldAnimateFAB(true);
  const handleFABAnimationComplete = () => setShouldAnimateFAB(false);

  const [isRefreshing, setIsRefreshing] = useState(false);

  useJournalSubDeepLink();

  useEffect(() => {
    if (!universalStatusCode || !appUserId) return;
    trackHomeTabViewed();
  }, [appUserId, trackHomeTabViewed, universalStatusCode]);

  const { data: fullProfileData, refetch: refetchFullProfile } =
    useGetFullProfileQuery();
  const { isCurrentOrSupportLinkedUserInTreatment, isCurrentUserInTreatment } =
    usePatientStatus(fullProfileData);
  const { activeModal } = useModalTracker();
  const {
    isBookingPermissionsFetched,
    patientIdsWithBookingPermissions,
    refetchSchedulePermissions,
  } = useSchedulePermissionsContext();
  const { presentHelpCenter } = useHelpCenter();

  const shouldShowBookAppointment =
    isCurrentOrSupportLinkedUserInTreatment &&
    isBookingPermissionsFetched &&
    // the Self Monitoring Tools FAB will be shown instead of the book appointment FAB when the self monitoring tools feature flag is enabled
    !shouldShowMealTracking &&
    !isEmpty(patientIdsWithBookingPermissions);

  useEffect(() => {
    if (isNil(basicInfoData)) return;
    const { chosenName, firstName } = basicInfoData;
    const preferredName = getPreferredFirstName({ chosenName, firstName });
    setName(preferredName);
  }, [basicInfoData]);

  useEffect(() => {
    if (isNil(fullProfileData)) return;
    // update user properties in mixpanel
    const userInfo = mapFullProfileToUserInfo(fullProfileData);
    updateUserInfo(userInfo);
    // parse notification settings from full profile data
    const userNotificationSettings = getNotificationSettings(fullProfileData);
    setNotificationSettings(userNotificationSettings);
  }, [fullProfileData, updateUserInfo]);

  const handleBookAppointment = () => {
    router.navigate(ModalPath.BookAppointment);
    trackStartBooking(TabSources.Home);
  };

  // to prevent a visible delay (~5s) on the explore tab,
  // we deliberately prefetch the content feed from the equip API here on the user's home tab
  // so that the API response is received and ready to be used by the time they switch to the explore tab
  const { contentFeed, refetchContentFeed } = useAppUserContentFeed();
  const mostPopularVideoResources = useMemo(
    () => shuffle(getMostPopularVideoResources(contentFeed)),
    [contentFeed],
  );
  const isUserInTreatmentOrSupport = isCurrentUserInTreatment || isSupport;
  const shouldShowExploreResourcesCarousel =
    isUserInTreatmentOrSupport && !isEmpty(mostPopularVideoResources);

  const shouldShowSMTWeekLogSection = shouldAppUserSeeWeekLogSection;

  const shouldShowSMTWelcomeModal =
    isRecommendedGroupCheckComplete &&
    activeModal !== ModalPath.RecommendedGroupClass &&
    !shouldShowWebRedesignOverview;

  const dispatch = useAppDispatch();

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    // Invalidate cache tags used by Home screen child components to force
    // their queries to refetch (UpcomingEventsSection, WeightTrackingSection, SMTWeekLogSection).
    // Scope invalidation to the app user and their proxy patients to avoid broad refetches
    // for unrelated cached entries elsewhere in the app.
    //
    // Schedule queries (AppointmentEvents/GroupClassEvents) use CacheIdType.AppUser for the
    // current user because useScheduleQueryPatientId maps appUserId → undefined, causing
    // getScheduleEvents to tag with CacheIdType.AppUser instead of the actual UUID.
    // Proxy patient schedule queries use the patient's actual externalId.
    //
    // Vitals/SMT queries bypass that mapping and always tag with the actual externalId.
    const proxyPatientIds = proxyPatients?.map((p) => p.externalId) ?? [];
    const vitalsAndSMTIds = [appUserId, ...proxyPatientIds].filter(Boolean);
    dispatch(
      api.util.invalidateTags([
        { id: CacheIdType.AppUser, type: CacheTagType.AppointmentEvents },
        { id: CacheIdType.AppUser, type: CacheTagType.GroupClassEvents },
        ...proxyPatientIds.flatMap((id) => [
          { id, type: CacheTagType.AppointmentEvents },
          { id, type: CacheTagType.GroupClassEvents },
        ]),
        ...vitalsAndSMTIds.flatMap((id) => [
          { id, type: CacheTagType.WeightObservations },
          { id, type: CacheTagType.SMTSummary },
          { id, type: CacheTagType.SMTEntries },
        ]),
      ]),
    );
    // Also refetch queries that don't use cache tags
    await Promise.allSettled([
      refetchBasicInfo(),
      refetchFullProfile(),
      refetchContentFeed(),
      refetchSMTPermissions(),
      refetchSchedulePermissions?.(),
      refetchTasks(),
    ]);
    setIsRefreshing(false);
  }, [
    appUserId,
    dispatch,
    proxyPatients,
    refetchBasicInfo,
    refetchFullProfile,
    refetchContentFeed,
    refetchSMTPermissions,
    refetchSchedulePermissions,
    refetchTasks,
  ]);

  const handleRefresh = useCallback(() => {
    void onRefresh();
  }, [onRefresh]);

  return (
    <ScreenContainer
      backgroundColor={isWeb() ? undefined : ColorToken.Primary10}
      shouldIgnoreSafeArea={shouldShowOnboarding}
    >
      <ScrollView
        backgroundColor={isWeb() ? undefined : ColorToken.Primary10}
        bounces
        // on web, maxWidth+center content; ScrollView stays full-width so scroll works viewport-wide.
        contentContainerStyle={WebContentConstraints}
        refreshControl={
          <RefreshControl
            colors={[ColorTokenValues[ColorToken.Primary100]]}
            onRefresh={handleRefresh}
            progressBackgroundColor={ColorTokenValues[ColorToken.Primary10]}
            refreshing={isRefreshing}
            tintColor={ColorTokenValues[ColorToken.Primary100]}
          />
        }
      >
        {shouldShowOnboarding ? (
          <OnboardingOverviewSection />
        ) : (
          <GreetingCard
            message="Welcome to your Equip account, your home for all things Equip."
            onHelpPress={() =>
              void presentHelpCenter({
                intercomPresenterSource: TabSources.Home,
              })
            }
            userName={name}
          />
        )}
        <VStack
          backgroundColor={ColorToken.NeutralBackgroundPrimary}
          gap={36}
          paddingBottom={ListDimensions.FABBottomOffset}
        >
          <TasksCarousel />
          {isUserInTreatmentOrSupport && <UpcomingEventsSection />}
          {/* v2 surfaces the recommended-groups carousel right below "Coming up" */}
          {isGroupClassRedesignEnabled && isUserInTreatmentOrSupport && (
            <RecommendedGroupsCarousel />
          )}
          <MealSupportHotlineSection tabSource={TabSources.Home} />
          {shouldShowSMTWeekLogSection && <SMTWeekLogSection />}
          {shouldShowExploreResourcesCarousel && (
            <ExploreResourcesCarousel
              data={mostPopularVideoResources}
              isMostPopularVideos
              title="Explore videos"
            />
          )}
          <WeightTrackingSection />
          <TestimonialImage />
          {/* v1 keeps the carousel here; v2 renders it below "Coming up" instead */}
          {!isGroupClassRedesignEnabled && isUserInTreatmentOrSupport && (
            <RecommendedGroupsCarousel />
          )}
          {isCurrentUserInTreatment ? (
            <RecommendedArticle />
          ) : (
            <RecommendedFAQ />
          )}
        </VStack>
      </ScrollView>
      {shouldShowBookAppointment && (
        <FloatingButton
          leftIcon={FAIconName.Calendar}
          onPress={handleBookAppointment}
          testID={FABTestID.BookAppointment}
          title="Book an appointment"
        />
      )}
      {shouldShowMealTracking && (
        <>
          <AddSMTEntryFlow
            onAnimationComplete={handleFABAnimationComplete}
            screenPath={TabPath.Home}
            shouldAnimateFAB={shouldAnimateFAB}
          />
          {shouldShowSMTWelcomeModal && (
            <SMTWelcomeModal
              isScreenFocused={isFocused}
              onAnimationTrigger={handleFABAnimationTrigger}
            />
          )}
        </>
      )}
      <WebRedesignOverviewModal
        isOpen={shouldShowWebRedesignOverview}
        onRequestClose={handleWebRedesignOverviewDismiss}
      />
      <DownloadEnforcementModal
        isDesktop={isDownloadEnforcementDesktop}
        isOpen={isDownloadEnforcementModalOpen}
        onClose={handleDownloadEnforcementClose}
        onDownload={handleDownloadEnforcementDownload}
      />
    </ScreenContainer>
  );
};

export default Home;
